import axios from "axios";
import { format } from "date-fns";

import { ApiResponse } from "../../models/api.response.model";
import { Error, ErrorResponse } from "../../models/error.model";
import { Message } from "../../models/message.model";
import { Company } from "../../models/company.model";
import { TokenResponse, TokenSettingsResponse } from "../../models/token.model";
import { CompanySettings } from "../../models/company.settings.model";
import { authToken } from "../../scripts/authentication.script";
import { currencyObject } from "../../libraries/currency.library";
import { extractBase64 } from "../../scripts/extract.base.64.script";
import { Brand } from "../../models/settings.brands.model";
import { Design } from "../../models/settings.designs.model";
import { Currency } from "../../models/settings.currency.model";
import { Feature, FeatureInputType, FeaturesData, FeatureType } from "../../models/settings.features.model";
import { MeasureUnit, MeasureUnitsData } from "../../models/settings.measure-units.model";
import { PaymentDeadline, PaymentDeadlinesData } from "../../models/settings.payment-deadlines.model";
import { DunningPolicy } from "../../models/settings.dunning-policies.model";
import { Template } from "../../models/template.model";
import { Settings } from "../../models/settings.model";

export class SettingsDA {
  static apiResponse: ApiResponse;

  public static async getSettings(): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let companySettings: CompanySettings | null = null;
      let error: Error | null = null;

      await axios
        .get("../../../../../database/settings.data.json")
        .then((response) => {
          companySettings = response.data.data;
        })
        .catch((error) => {
          alert(error);
        });
      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data;
        })
        .catch((error) => {
          alert(error);
        });

      if (companySettings) {
        this.apiResponse = {
          status: 200,
          message: "Success",
          data: companySettings,
        };
      } else {
        this.apiResponse = {
          status: 400,
          message: "Bad request",
          data: error,
        };
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/settings`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      await axios
        .get(url, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.data ?? response.data,
          };
        })
        .catch((error) => {
          this.apiResponse = {
            status: error.response?.status || 500,
            message: error.response?.statusText || "Error",
            data: error.response?.data?.error ?? error.response?.data ?? { code: "NETWORK_ERROR", message: error.message },
          };
        });
    }

    return this.apiResponse;
  }

  public static async postSettings(
    api_key: string,
    name: string,
    logo: any,
    street: string,
    street_number: string,
    additional: string,
    administrative_area: string,
    city: string,
    zip_code: string,
    country: string,
    email: string,
    phone: string,
    website: string,
    register_number: string,
    vat_number: string,
    currency_code: string,
    subject_tax: boolean,
    tax_declaration: string,
    tax_method: string,
    tax_rate: string,
    language: string,
    qr_type: string,
    declaration_periodicity: string = '',
    fiscal_year_start: string = '',
    fiscal_year_end: string = '',
    legal_form: string = '',
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let tokenSettingsResponse: TokenSettingsResponse | null = null;
      let errorResponse: ErrorResponse | null = null;

      await axios
        .get("../../../../../database/token.settings.data.json")
        .then((response) => {
          tokenSettingsResponse = response.data;
        })
        .catch((error) => {
          alert(error);
        });
      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          errorResponse = response.data;
        })
        .catch((error) => {
          alert(error);
        });

      if (tokenSettingsResponse!.data && "company" in tokenSettingsResponse!.data) {
        let company = tokenSettingsResponse!.data.company!;

        if ("email" in company) {
          company.name = name;
          company.logo = logo && logo.length > 0 ? logo : "";
          company.email = email;
          company.phone = phone;
          company.website = website;
          company.register_number = register_number;
          company.vat_number = vat_number;
          company.currency = currencyObject(language, currency_code);
          company.subject_tax = subject_tax;
          company.tax_declaration = tax_declaration;
          company.tax_method = tax_method;
          company.language = language;
          company.qr_type = qr_type;
          company.created_at = `${format(new Date(), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)}`;
          company.updated_at = `${format(new Date(), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)}`;
          company.address.street = street;
          company.address.number = street_number;
          company.address.additional = additional;
          company.address.administrative_area = administrative_area;
          company.address.city = city;
          company.address.zip_code = zip_code;
          company.address.country = country;

          if (logo && typeof logo !== "string") {
            extractBase64(logo).then((image: any) => {
              company.logo = image.base;
            });
          }
        }

        this.apiResponse = {
          status: 201,
          message: "Success",
          data: tokenSettingsResponse!.success === true ? tokenSettingsResponse!.data : null,
        };
      } else {
        this.apiResponse = {
          status: 400,
          message: "Bad request",
          data: errorResponse!.success === false ? errorResponse!.error : null,
        };
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/settings`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      let data: any = new FormData();
      data.append("logo", logo);
      data.append("name", name);
      data.append("phone", phone);
      data.append("email", email);
      data.append("website", website);
      data.append("register_number", register_number);
      data.append("vat_number", vat_number);
      data.append("street", street);
      data.append("number", street_number);
      data.append("additional", additional);
      data.append("administrative_area", administrative_area);
      data.append("city", city);
      data.append("zip_code", zip_code);
      data.append("country", country);
      data.append("currency_code", currency_code);
      data.append("language", language);
      data.append("qr_type", qr_type);
      if (legal_form) data.append("legal_form", legal_form);
      data.append("meta_data", JSON.stringify({
        subject_tax: !!subject_tax,
        tax_method: tax_method || '',
        tax_rate: Number(tax_rate) || 0,
        tax_declaration: tax_declaration || '',
        declaration_periodicity: declaration_periodicity || '',
        ...(fiscal_year_start ? { fiscal_year_start } : {}),
        ...(fiscal_year_end ? { fiscal_year_end } : {}),
      }));

      await axios
        .post(url, data, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.data ?? response.data,
          };
        })
        .catch((error) => {
          this.apiResponse = {
            status: error.response?.status ?? 500,
            message: error.response?.statusText ?? 'Error',
            data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
          };
        });
    }

    return this.apiResponse;
  }

  public static async putSettings(
    qr_type: string,
    language: string,
    colors: string[],
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let settings: CompanySettings | null = null;
      let message: Message | null = null;
      let error: Error | null = null;

      await axios
        .get("../../../../../database/settings.data.json")
        .then((response) => {
          settings = response.data.data;
        })
        .catch((error) => {
          alert(error);
        });

      await axios
        .get("../../../../../database/message.data.json")
        .then((response) => {
          message = response.data;
        })
        .catch((error) => {
          alert(error);
        });

      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data;
        })
        .catch((error) => {
          alert(error);
        });

      const hexRegex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
      const colorsAreValid = colors.length > 0 && colors.every((color) => hexRegex.test(color));

      if (settings && colorsAreValid) {
        this.apiResponse = {
          status: 200,
          message: "Success",
          data: {
            ...(settings as CompanySettings),
            qr_type,
            language,
            colors,
          },
        };
      } else {
        this.apiResponse = {
          status: 400,
          message: "Bad request",
          data: error,
        };
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/settings`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      let data: any = {
        qr_type,
        language,
        colors: colors,
      };

      await axios
        .put(url, data, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.data ?? response.data,
          };
        })
        .catch((error) => {
          this.apiResponse = {
            status: error.response?.status || 500,
            message: error.response?.statusText || "Error",
            data: error.response?.data?.error ?? error.response?.data ?? { code: "NETWORK_ERROR", message: error.message },
          };
        });
    }

    return this.apiResponse;
  }

  public static async putSettingsParameters(
    id_company: string,
    deadline: { type: string; quantity: string } | null,
    unit: { type: string; name: string; symbol: string } | null,
    feature: { type: FeatureType; name: string; input_type: FeatureInputType; options: string[] } | null,
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let companySettings: CompanySettings | null = null;
      let error: Error | null = null;

      await axios
        .get("../../../../../database/settings.data.json")
        .then((response) => {
          companySettings = response.data;
        })
        .catch((error) => {
          alert(error);
        });
      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data;
        })
        .catch((error) => {
          alert(error);
        });

      if (companySettings!.id_company === id_company) {
        if (deadline) {
          companySettings!.data.payment_deadlines.push({
            id: `deadline${Math.floor(Math.random() * 1000).toString()}`,
            type: deadline.type,
            quantity: Number(deadline.quantity),
          });
        }
        if (unit) {
          companySettings!.data.measure_units.push({
            id: `unit${Math.floor(Math.random() * 1000).toString()}`,
            type: unit.type,
            name: unit.name,
            symbol: unit.symbol,
          });
        }
        if (feature) {
          companySettings!.data.features.push({
            id: `feature${Math.floor(Math.random() * 1000).toString()}`,
            type: feature.type,
            name: feature.name,
            input_type: feature.input_type,
            options: feature.options,
          });
        }

        this.apiResponse = {
          status: 200,
          message: "Success",
          data: companySettings!.data,
        };
      } else {
        this.apiResponse = {
          status: 400,
          message: "Bad request",
          data: error,
        };
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/settings/parameters`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      let data: any = {
        payment_deadlines: deadline ? { ...deadline, quantity: Number(deadline.quantity) } : null,
        measure_units: unit,
        feature: feature,
      };

      await axios
        .put(url, data, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.data ?? response.data,
          };
        })
        .catch((error) => {
          this.apiResponse = {
            status: error.response?.status || 500,
            message: error.response?.statusText || "Error",
            data: error.response?.data?.error ?? error.response?.data ?? { code: "NETWORK_ERROR", message: error.message },
          };
        });
    }

    return this.apiResponse;
  }

  public static async getSettingPaymentDeadlines(id_company: string, page: number = 1, limit: number = 10): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let allItems: PaymentDeadline[] | null = null;
      let error: Error | null = null;

      await axios
        .get("../../../../../database/settings.payment-deadlines.data.json")
        .then((response) => {
          allItems = response.data.data.items;
        })
        .catch((error) => {
          alert(error);
        });

      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data;
        })
        .catch((error) => {
          alert(error);
        });

      if (allItems) {
        const total = (allItems as PaymentDeadline[]).length;
        const pages = limit > 0 ? Math.ceil(total / limit) : 1;
        const safePage = Math.min(Math.max(page, 1), pages || 1);
        const start = (safePage - 1) * limit;
        const items = (allItems as PaymentDeadline[]).slice(start, start + limit);

        this.apiResponse = {
          status: 200,
          message: "Success",
          data: {
            items,
            pagination: { page: safePage, limit, total, pages },
          } as PaymentDeadlinesData,
        };
      } else {
        this.apiResponse = {
          status: 400,
          message: "Bad request",
          data: error,
        };
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/payment-deadlines?page=${page}&limit=${limit}`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      await axios
        .get(url, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.data ?? response.data,
          };
        })
        .catch((error) => {
          this.apiResponse = {
            status: error.response?.status || 500,
            message: error.response?.statusText || "Error",
            data: error.response?.data?.error ?? error.response?.data ?? { code: "NETWORK_ERROR", message: error.message },
          };
        });
    }

    return this.apiResponse;
  }

  public static async postSettingPaymentDeadline(id_company: string, type: string, quantity: string): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let deadlinesResponse: PaymentDeadlinesData = { items: [], pagination: { page: 1, limit: 10, total: 0, pages: 0 } };
      let error: Error | null = null;

      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data;
        })
        .catch((err) => {
          alert(err);
        });

      if (deadlinesResponse) {
        const newDeadline: PaymentDeadline = {
          id: `dl_${Math.floor(Math.random() * 1000).toString()}`,
          type: type,
          quantity: Number(quantity),
        };

        this.apiResponse = {
          status: 201,
          message: "Success",
          data: newDeadline,
        };
      } else {
        this.apiResponse = {
          status: 400,
          message: "Bad request",
          data: error,
        };
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/payment-deadlines`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      let data: any = {
        type,
        quantity: Number(quantity),
      };

      await axios
        .post(url, data, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.data ?? response.data,
          };
        })
        .catch((err) => {
          this.apiResponse = {
            status: err.response?.status || 500,
            message: err.response?.statusText || "Error",
            data: err.response?.data?.error ?? err.response?.data ?? { code: "NETWORK_ERROR", message: err.message },
          };
        });
    }

    return this.apiResponse;
  }

  public static async putSettingPaymentDeadline(id_company: string, id_deadline: string, type: string, quantity: string): Promise<ApiResponse> {

    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let deadlinesResponse: PaymentDeadlinesData = { items: [], pagination: { page: 1, limit: 10, total: 0, pages: 0 } };
      let error: Error | null = null;

      await axios
        .get("../../../../../database/settings.payment-deadlines.data.json")
        .then((response) => {
          deadlinesResponse = response.data.data;
        })
        .catch((err) => {
          alert(err);
        });
      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data;
        })
        .catch((err) => {
          alert(err);
        });

      const itemFound = deadlinesResponse.items.find((item) => item.id === id_deadline);

      if (itemFound) {
        itemFound.type = type;
        itemFound.quantity = +quantity;

        this.apiResponse = {
          status: 200,
          message: "Success",
          data: itemFound,
        };
      } else {
        this.apiResponse = {
          status: 400,
          message: "Bad request",
          data: error,
        };
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/payment-deadlines/${id_deadline}`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      let data: any = {
        type,
        quantity: Number(quantity),
      };

      await axios
        .put(url, data, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.data ?? response.data,
          };
        })
        .catch((err) => {
          this.apiResponse = {
            status: err.response?.status || 500,
            message: err.response?.statusText || "Error",
            data: err.response?.data?.error ?? err.response?.data ?? { code: "NETWORK_ERROR", message: err.message },
          };
        });
    }

    return this.apiResponse;
  }

  public static async deleteSettingPaymentDeadline(id_company: string, id_deadline: string): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let deadlinesResponse: PaymentDeadlinesData = { items: [], pagination: { page: 1, limit: 10, total: 0, pages: 0 } };
      let error: Error | null = null;

      await axios
        .get("../../../../../database/settings.payment-deadlines.data.json")
        .then((response) => {
          deadlinesResponse = response.data.data;
        })
        .catch((err) => {
          alert(err);
        });
      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data;
        })
        .catch((err) => {
          alert(err);
        });

      const itemFound = deadlinesResponse.items.find((item) => item.id === id_deadline);

      if (itemFound) {
        deadlinesResponse.items = deadlinesResponse.items.filter((item) => item.id !== id_deadline);

        this.apiResponse = {
          status: 200,
          message: "Success",
          data: { message: "DEADLINE_DELETED" },
        };
      } else {
        this.apiResponse = {
          status: 400,
          message: "Bad request",
          data: error,
        };
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/payment-deadlines/${id_deadline}`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      await axios
        .delete(url, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.data ?? response.data,
          };
        })
        .catch((err) => {
          this.apiResponse = {
            status: err.response?.status || 500,
            message: err.response?.statusText || "Error",
            data: err.response?.data?.error ?? err.response?.data ?? { code: "NETWORK_ERROR", message: err.message },
          };
        });
    }

    return this.apiResponse;
  }

  public static async getSettingMeasureUnits(id_company: string,page: number = 1, limit: number = 10): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let allItems: MeasureUnit[] | null = null;
      let error: Error | null = null;

      await axios
        .get("../../../../../database/settings.measure-units.data.json")
        .then((response) => {
          allItems = response.data.data.items;
        })
        .catch((error) => {
          alert(error);
        });

      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data;
        })
        .catch((error) => {
          alert(error);
        });

      if (allItems) {
        const total = (allItems as MeasureUnit[]).length;
        const pages = limit > 0 ? Math.ceil(total / limit) : 1;
        const safePage = Math.min(Math.max(page, 1), pages || 1);
        const start = (safePage - 1) * limit;
        const items = (allItems as MeasureUnit[]).slice(start, start + limit);

        this.apiResponse = {
          status: 200,
          message: "Success",
          data: {
            items,
            pagination: { page: safePage, limit, total, pages },
          } as MeasureUnitsData,
        };
      } else {
        this.apiResponse = {
          status: 400,
          message: "Bad request",
          data: error,
        };
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/measure-units?page=${page}&limit=${limit}`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      await axios
        .get(url, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.data ?? response.data,
          };
        })
        .catch((error) => {
          this.apiResponse = {
            status: error.response?.status || 500,
            message: error.response?.statusText || "Error",
            data: error.response?.data?.error ?? error.response?.data ?? { code: "NETWORK_ERROR", message: error.message },
          };
        });
    }

    return this.apiResponse;
  }

  public static async postSettingMeasureUnit(id_company: string, type: string, name: string, symbol: string): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let error: Error | null = null;

      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data.data;
        })
        .catch((err) => {
          alert(err);
        });

      const newUnit: MeasureUnit = {
        id: `unit${Math.floor(Math.random() * 1000).toString()}`,
        type,
        name,
        symbol,
      };

      this.apiResponse = {
        status: 201,
        message: "Success",
        data: newUnit,
      };
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/measure-units`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      let data: any = { type, name, symbol };

      await axios
        .post(url, data, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.data ?? response.data,
          };
        })
        .catch((err) => {
          this.apiResponse = {
            status: err.response?.status || 500,
            message: err.response?.statusText || "Error",
            data: err.response?.data?.error ?? err.response?.data ?? { code: "NETWORK_ERROR", message: err.message },
          };
        });
    }

    return this.apiResponse;
  }

  public static async putSettingMeasureUnit(id_company: string, id_unit: string, type: string, name: string, symbol: string): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let error: Error | null = null;

      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data;
        })
        .catch((err) => {
          alert(err);
        });

      this.apiResponse = {
        status: 200,
        message: "Success",
        data: { id: id_unit, type, name, symbol },
      };
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/measure-units/${id_unit}`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      let data: any = { type, name, symbol };

      await axios
        .put(url, data, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.data ?? response.data,
          };
        })
        .catch((err) => {
          this.apiResponse = {
            status: err.response?.status || 500,
            message: err.response?.statusText || "Error",
            data: err.response?.data?.error ?? err.response?.data ?? { code: "NETWORK_ERROR", message: err.message },
          };
        });
    }

    return this.apiResponse;
  }

  public static async deleteSettingMeasureUnit(id_company:string, id_unit: string): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let error: Error | null = null;

      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data;
        })
        .catch((err) => {
          alert(err);
        });

      this.apiResponse = {
        status: 200,
        message: "Success",
        data: { message: "MEASURE_UNIT_DELETED" },
      };
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/measure-units/${id_unit}`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      await axios
        .delete(url, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.data ?? response.data,
          };
        })
        .catch((err) => {
          this.apiResponse = {
            status: err.response?.status || 500,
            message: err.response?.statusText || "Error",
            data: err.response?.data?.error ?? err.response?.data ?? { code: "NETWORK_ERROR", message: err.message },
          };
        });
    }

    return this.apiResponse;
  }

  public static async getSettingFeatures(id_company:string, page: number = 1, limit: number = 10): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let allItems: Feature[] | null = null;
      let error: Error | null = null;

      await axios
        .get("../../../../../database/settings.features.data.json")
        .then((response) => {
          allItems = response.data.data.items;
        })
        .catch((error) => {
          alert(error);
        });

      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data;
        })
        .catch((error) => {
          alert(error);
        });

      if (allItems) {
        const total = (allItems as Feature[]).length;
        const pages = limit > 0 ? Math.ceil(total / limit) : 1;
        const safePage = Math.min(Math.max(page, 1), pages || 1);
        const start = (safePage - 1) * limit;
        const items = (allItems as Feature[]).slice(start, start + limit);

        this.apiResponse = {
          status: 200,
          message: "Success",
          data: {
            items,
            pagination: { page: safePage, limit, total, pages },
          } as FeaturesData,
        };
      } else {
        this.apiResponse = {
          status: 400,
          message: "Bad request",
          data: error,
        };
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/features?page=${page}&limit=${limit}`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      await axios
        .get(url, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.data ?? response.data,
          };
        })
        .catch((error) => {
          this.apiResponse = {
            status: error.response?.status || 500,
            message: error.response?.statusText || "Error",
            data: error.response?.data?.error ?? error.response?.data ?? { code: "NETWORK_ERROR", message: error.message },
          };
        });
    }

    return this.apiResponse;
  }

  public static async postSettingFeature(
    id_company: string,
    type: FeatureType,
    name: string,
    input_type: FeatureInputType,
    options: string[],
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let error: Error | null = null;

      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data;
        })
        .catch((err) => {
          alert(err);
        });

      const newFeature: Feature = {
        id: `feature${Math.floor(Math.random() * 1000).toString()}`,
        type,
        name,
        input_type,
        options,
      };

      this.apiResponse = {
        status: 201,
        message: "Success",
        data: newFeature,
      };
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/features`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      let data: any = { type, name, options };

      await axios
        .post(url, data, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.data ?? response.data,
          };
        })
        .catch((err) => {
          this.apiResponse = {
            status: err.response?.status || 500,
            message: err.response?.statusText || "Error",
            data: err.response?.data?.error ?? err.response?.data ?? { code: "NETWORK_ERROR", message: err.message },
          };
        });
    }

    return this.apiResponse;
  }

  public static async putSettingFeature(id_company: string, id_feature: string, type: string, name: string, options: string[]): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let error: Error | null = null;

      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data.data;
        })
        .catch((err) => {
          alert(err);
        });

      this.apiResponse = {
        status: 200,
        message: "Success",
        data: { id: id_feature, type, name, options },
      };
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/features/${id_feature}`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      let data: any = { type, name, options };

      await axios
        .put(url, data, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.data ?? response.data,
          };
        })
        .catch((err) => {
          this.apiResponse = {
            status: err.response?.status || 500,
            message: err.response?.statusText || "Error",
            data: err.response?.data?.error ?? err.response?.data ?? { code: "NETWORK_ERROR", message: err.message },
          };
        });
    }

    return this.apiResponse;
  }

  public static async deleteSettingFeature(id_company:string, id_feature: string): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let error: Error | null = null;

      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data.data;
        })
        .catch((err) => {
          alert(err);
        });

      this.apiResponse = {
        status: 200,
        message: "Success",
        data: { message: "FEATURE_DELETED" },
      };
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/features/${id_feature}`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      await axios
        .delete(url, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data?.data ?? response.data,
          };
        })
        .catch((err) => {
          this.apiResponse = {
            status: err.response?.status || 500,
            message: err.response?.statusText || "Error",
            data: err.response?.data?.error ?? err.response?.data ?? { code: "NETWORK_ERROR", message: err.message },
          };
        });
    }

    return this.apiResponse;
  }

  public static async getSettingCurrencies(id_company: string): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      let currencies: Currency[] | null = null;
      let error: Error | null = null;

      await axios
        .get("../../../../../database/settings.currencies.data.json")
        .then((response) => {
          currencies = response.data;
        })
        .catch((error) => {
          alert(error);
        });
      await axios
        .get("../../../../../database/error.data.json")
        .then((response) => {
          error = response.data;
        })
        .catch((error) => {
          alert(error);
        });

      if (currencies) {
        this.apiResponse = {
          status: 200,
          message: "Success",
          data: currencies,
        };
      } else {
        this.apiResponse = {
          status: 400,
          message: "Bad request",
          data: error,
        };
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/currencies`;

      let config: any = {
        headers: { Authorization: authToken() },
      };

      await axios
        .get(url, config)
        .then((response) => {
          this.apiResponse = {
            status: response.status,
            message: response.statusText,
            data: response.data ?? [],
          };
        })
        .catch((error) => {
          this.apiResponse = {
            status: error.response?.status || 500,
            message: error.response?.statusText || "Error",
            data: error.response?.data?.error ?? error.response?.data ?? { code: "NETWORK_ERROR", message: error.message },
          };
        });
    }

    return this.apiResponse;
  }

  public static async getDunningPolicies(id_company: string, page: number = 1, limit: number = 50): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/dunning-policies?page=${page}&limit=${limit}`;

    let config: any = {
      headers: { Authorization: authToken() },
    };

    await axios
      .get(url, config)
      .then((response) => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        };
      })
      .catch((error) => {
        this.apiResponse = {
          status: error.response?.status || 500,
          message: error.response?.statusText || "Error",
          data: error.response?.data?.error ?? error.response?.data ?? { code: "NETWORK_ERROR", message: error.message },
        };
      });

    return this.apiResponse;
  }

  public static async postDunningPolicy(id_company: string, policy: Partial<DunningPolicy>): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/dunning-policies`;

    let config: any = {
      headers: { Authorization: authToken() },
    };

    let data: any = {
      label: policy.label,
      is_default: policy.is_default ?? false,
      steps: policy.steps ?? [],
      stop_after_last_step: policy.stop_after_last_step ?? true,
    };

    await axios
      .post(url, data, config)
      .then((response) => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        };
      })
      .catch((err) => {
        this.apiResponse = {
          status: err.response?.status || 500,
          message: err.response?.statusText || "Error",
          data: err.response?.data?.error ?? err.response?.data ?? { code: "NETWORK_ERROR", message: err.message },
        };
      });

    return this.apiResponse;
  }

  public static async putDunningPolicy(id_company: string, id_policy: string, policy: Partial<DunningPolicy>): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/dunning-policies/${id_policy}`;

    let config: any = {
      headers: { Authorization: authToken() },
    };

    let data: any = {
      label: policy.label,
      is_default: policy.is_default,
      steps: policy.steps,
      stop_after_last_step: policy.stop_after_last_step,
    };

    await axios
      .put(url, data, config)
      .then((response) => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        };
      })
      .catch((err) => {
        this.apiResponse = {
          status: err.response?.status || 500,
          message: err.response?.statusText || "Error",
          data: err.response?.data?.error ?? err.response?.data ?? { code: "NETWORK_ERROR", message: err.message },
        };
      });

    return this.apiResponse;
  }

  public static async deleteDunningPolicy(id_company: string, id_policy: string): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/dunning-policies/${id_policy}`;

    let config: any = {
      headers: { Authorization: authToken() },
    };

    await axios
      .delete(url, config)
      .then((response) => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        };
      })
      .catch((err) => {
        this.apiResponse = {
          status: err.response?.status || 500,
          message: err.response?.statusText || "Error",
          data: err.response?.data?.error ?? err.response?.data ?? { code: "NETWORK_ERROR", message: err.message },
        };
      });

    return this.apiResponse;
  }
}
